import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
  OnInit,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { RouterOutlet, RouterLink, Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { AuthService } from '@auth0/auth0-angular';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { RouterUrlsEnum } from '../../enums/router-urls.enum';
import { DarkModeToggleComponent } from '../../theme/dark-mode-toggle.component';
import { filter, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';

interface NavItemI {
  path: string;
  label: string;
  icon: string;
  matchSegments: string[];
}

interface PageInfo {
  title: string;
  subtitle: string;
}

const TITLE_MAP: Record<string, PageInfo> = {
  'admin-dashboard': { title: 'Operations overview', subtitle: 'Last 7 days' },
  'manage-events': { title: 'Testing events', subtitle: 'Calendar · North Texas' },
  'manage-customers': { title: 'Members', subtitle: 'All regions' },
  'manage-bookings': { title: 'Bookings', subtitle: 'All regions' },
  'manage-event-waitlist': { title: 'Waitlist', subtitle: 'Per-event demand' },
  'manage-reservations': { title: 'Reservations', subtitle: 'Bring my truck leads' },
  'manage-clubs': { title: 'Partner gyms', subtitle: 'Active locations' },
  'manage-bodymeasurements': { title: 'Reports', subtitle: 'Body measurements' },
  'manage-payments': { title: 'Payments', subtitle: 'Revenue & utilization' },
  'manage-financial-reports': { title: 'Financial reports', subtitle: 'Revenue, refunds & CSV export' },
  'manage-orders': { title: 'Orders', subtitle: 'All orders' },
  'manage-settings': { title: 'Settings', subtitle: 'Organization' },
  'manage-signups': { title: 'Sign-ups', subtitle: 'Registration pipeline' },
  'manage-discounts': { title: 'Discounts', subtitle: 'Promo codes' },
  'manage-contact-requests': { title: 'Contact Requests', subtitle: 'Marketing leads' },
  'manage-leaderboards': { title: 'Leaderboards & Challenges', subtitle: 'Member engagement' },
  'manage-regions': { title: 'Regions', subtitle: 'Coverage areas' },
  'manage-tenants': { title: 'Tenants', subtitle: 'Organizations' },
  // The live tenant route is `/tenants/...` (manageTenantUiRoutes, path: 'tenants'),
  // not `/auth/manage-tenants`. Map the real segment so the topbar title resolves.
  tenants: { title: 'Tenants', subtitle: 'Organizations' },
  'manage-operators': { title: 'Operators', subtitle: 'Truck staff' },
  'manage-users': { title: 'Users', subtitle: 'All users' },
  'admin-setup': { title: 'Company setup', subtitle: 'Configuration' },
  'admin-register': { title: 'Registration', subtitle: 'Operator onboarding' },
  license: { title: 'License', subtitle: 'Subscription management' },
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-admin-shell',
  templateUrl: './admin-shell.component.html',
  // Two sheets on purpose: the admin app's 6 kB `anyComponentStyle` budget is
  // enforced per file. Order matters — the mobile sheet's @media overrides must
  // cascade after the desktop rules.
  styleUrls: ['./admin-shell.component.scss', './admin-shell.mobile.scss'],
  imports: [RouterOutlet, RouterLink, AsyncPipe, ProfileMenuComponent, DarkModeToggleComponent],
})
export class AdminShellComponent implements OnInit, OnDestroy {
  /** #2246 — slim internal footer year (marketing FooterComponent removed from the shell). */
  protected readonly currentYear = new Date().getFullYear();

  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  protected auth = inject(AuthService);

  /**
   * Admin's real account-settings route (profileUiRoutes, registered under
   * `auth/profile/details` since #2136 removed the dead `account` child — see
   * libs/front/shared/profile-ui/src/index.ts).
   * ProfileMenuComponent's default (AUTH_ACCOUNT, '/auth/account') is the
   * customer app's route and 404s here, falling through to the admin app's
   * `**` wildcard redirect to `home`.
   */
  protected readonly profileAccountUrl = RouterUrlsEnum.AUTH_PROFILE_ACCOUNT;

  /**
   * Horizontal top-nav model (replaces the old left sidebar). The primary
   * destinations render as flat links; the `Operations` cluster collapses into a
   * dropdown; and the former `Configuration` cluster (Regions, License, Tenants)
   * now lives inside the Settings hub (`/auth/manage-settings`) — so `Settings`
   * is the single right-aligned entry that also highlights for those routes.
   *
   * On mobile (<=900px, where this horizontal nav hides — see
   * admin-shell.mobile.scss) there is no in-shell navigation chrome besides the
   * brand logo; the Overview page's Manage grid is the mobile navigation
   * surface (founder decision, admin-overview-mobile-fixes).
   */
  readonly primaryNav: NavItemI[] = [
    { path: '/auth/admin-dashboard', label: 'Overview', icon: 'bx-grid-alt', matchSegments: ['admin-dashboard'] },
    { path: '/auth/manage-events', label: 'Events', icon: 'bx-calendar', matchSegments: ['manage-events'] },
    { path: '/auth/manage-customers', label: 'Members', icon: 'bx-group', matchSegments: ['manage-customers'] },
    { path: '/auth/manage-clubs', label: 'Partner gyms', icon: 'bx-buildings', matchSegments: ['manage-clubs'] },
    {
      path: '/auth/manage-bodymeasurements',
      label: 'Reports',
      icon: 'bx-line-chart',
      matchSegments: ['manage-bodymeasurements'],
    },
  ];

  readonly operationsNav: NavItemI[] = [
    { path: '/auth/manage-bookings', label: 'Bookings', icon: 'bx-book-open', matchSegments: ['manage-bookings'] },
    // Waitlist joins Bookings/Events here rather than the capped 5-item
    // primaryNav (same rationale as Challenges below).
    {
      path: '/auth/manage-event-waitlist',
      label: 'Waitlist',
      icon: 'bx-list-ol',
      matchSegments: ['manage-event-waitlist'],
    },
    // #2909: "bring my truck" leads. primaryNav (which holds Events) is a
    // capped 5-item list — same rationale as Challenges below — so Reservations
    // joins the Operations cluster next to Bookings/Waitlist instead.
    {
      path: '/auth/manage-reservations',
      label: 'Reservations',
      icon: 'bx-truck',
      matchSegments: ['manage-reservations'],
    },
    // #2642 item 12: was previously unreachable except by typing the URL — it
    // only piggybacked on Reports' matchSegments for active-state highlighting,
    // with no link of its own.
    { path: '/auth/manage-payments', label: 'Payments', icon: 'bx-credit-card', matchSegments: ['manage-payments'] },
    // #2628 Tier-3: revenue/refunds by day + by event, with CSV export. Joins
    // Payments here (same "reports" territory) rather than the capped
    // 5-item primaryNav.
    {
      path: '/auth/manage-financial-reports',
      label: 'Financial reports',
      icon: 'bx-line-chart',
      matchSegments: ['manage-financial-reports'],
    },
    { path: '/auth/manage-orders', label: 'Orders', icon: 'bx-receipt', matchSegments: ['manage-orders'] },
    {
      path: '/auth/manage-discounts',
      label: 'Discounts',
      icon: 'bx-purchase-tag',
      matchSegments: ['manage-discounts'],
    },
    // #2909: admin inbox for marketing contact-form leads, joins Discounts
    // here (same capped-primaryNav rationale as Challenges below).
    {
      path: '/auth/manage-contact-requests',
      label: 'Contact Requests',
      icon: 'bx-envelope',
      matchSegments: ['manage-contact-requests'],
    },
    // #1868: carve-out from #1773 sub-item 7 — operators previously had no
    // list/edit UI at all (only a create-only Settings wizard step).
    { path: '/auth/manage-operators', label: 'Operators', icon: 'bx-id-card', matchSegments: ['manage-operators'] },
    // #2223: Progress Challenges/leaderboards — a periodic engagement/growth
    // task (create a challenge, monitor a roster) rather than a daily core
    // view, so it joins Discounts here instead of the capped 5-item primaryNav.
    {
      path: '/auth/manage-leaderboards',
      label: 'Challenges',
      icon: 'bx-trophy',
      matchSegments: ['manage-leaderboards'],
    },
  ];

  /**
   * Settings — also the home for the former Configuration cluster, so it
   * highlights when viewing Regions / License / Tenants (reached via the hub).
   */
  readonly settingsNav: NavItemI = {
    path: '/auth/manage-settings',
    label: 'Settings',
    icon: 'bx-cog',
    matchSegments: ['manage-settings', 'admin-setup', 'manage-regions', 'license', 'tenants'],
  };

  pageTitle = 'Admin';
  pageSubtitle = '';

  /**
   * True on routes whose leaf component renders its own page header (via
   * `AdmPageHeaderComponent`) — showing the shell's data-driven band as well
   * stacks two headers, wasting ~250px of the phone viewport (P2 dark/mobile QA).
   *
   * Driven by route data (`data: { ownHeader: true }`) rather than the old
   * URL-substring allowlist. Route data is resolved BEFORE the leaf component
   * renders, so the band never flashes and there is no NG0100 hazard — a child
   * component setting a flag the already-checked shell binding reads would hit
   * exactly that. It also lets features migrate to the shared header one at a
   * time: an unmigrated route simply omits the flag and keeps the shell band.
   *
   * A signal (matching `operationsOpen` below) so the OnPush view reliably
   * re-renders when it flips on navigation.
   */
  readonly hideHeaderBand = signal(false);

  /** Desktop "Operations" dropdown state. */
  readonly operationsOpen = signal(false);

  toggleOperations(event: Event): void {
    // Stop the bubble so the document:click handler below doesn't immediately re-close.
    event.stopPropagation();
    this.operationsOpen.update(open => !open);
  }

  /** Any click outside the open dropdown dismisses it. */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.operationsOpen()) {
      this.operationsOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.operationsOpen.set(false);
  }

  private routeSub: Subscription | null = null;

  ngOnInit(): void {
    this.updatePageInfo(this.router.url);
    this.routeSub = this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(e => (e as NavigationEnd).urlAfterRedirects),
      )
      .subscribe(url => {
        this.updatePageInfo(url);
        // Close the Operations dropdown after navigating so the new page is visible.
        this.operationsOpen.set(false);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  isNavItemActive(item: NavItemI): boolean {
    return item.matchSegments.some(seg => this.router.url.includes(seg));
  }

  /** True when the current route belongs to the Operations cluster (highlights the dropdown). */
  isOperationsActive(): boolean {
    return this.operationsNav.some(item => this.isNavItemActive(item));
  }

  /**
   * True when any route on the currently-activated branch declares
   * `data: { ownHeader: true }`.
   *
   * Reads `router.routerState.snapshot`, NOT an injected ActivatedRoute: this
   * shell is rendered by AppComponent, outside the `<router-outlet>`, so an
   * injected ActivatedRoute resolves against a non-routed context and its
   * `firstChild` chain does not reliably reflect the activated tree — the band
   * stayed visible on some feature areas and not others. The router's own
   * snapshot is authoritative wherever the component sits.
   *
   * Every level is checked rather than just the leaf: Angular only inherits
   * route `data` down the tree when `paramsInheritanceStrategy` is 'always'.
   */
  private leafRendersOwnHeader(): boolean {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      if (route.data['ownHeader'] === true) {
        return true;
      }
      route = route.firstChild;
    }
    return false;
  }

  private updatePageInfo(url: string): void {
    this.hideHeaderBand.set(this.leafRendersOwnHeader());

    const segments = url.split('/').filter(Boolean);
    for (const seg of segments) {
      if (TITLE_MAP[seg]) {
        this.pageTitle = TITLE_MAP[seg].title;
        this.pageSubtitle = TITLE_MAP[seg].subtitle;
        return;
      }
    }
    this.pageTitle = 'Admin';
    this.pageSubtitle = 'Admin console';
  }
}
